import { formatRowForRendering } from '@/utils/results';
import Vue from 'vue';

const initialCan = {
  approve: true,
  delete: true,
  edit: true,
  view: true,
};

const resolveRegeneratedResultChannelName = ({ uuid, version }) => `research.results_regenerated.${uuid}.${version}`;

async function listenForRecalculations(result, regeneratedCallback, regeneratingCallback) {
  const channelName = resolveRegeneratedResultChannelName(result);

  const Echo = await lazyLoadEcho();

  Echo.join(channelName)
    .listen('.research.results_regenerated', regeneratedCallback)
    .listen('.research.results_regenerating', regeneratingCallback);
}

async function lazyLoadEcho() {
  return (await import('../../../services/broadcasting/echo')).default;
}

export const ProjectResults = {
  namespaced: true,

  state: {
    current: null,
    entries: null,
    metadataFields: [],
    data: null,
    layoutData: [],
    layoutPagination: {},
    loading: false,
    saving: false,
    currentFilters: {},
    layoutEntries: [],
    pagination: {},
    can: { ...initialCan },
    regenerating: false,
    approving: false,
    editingEntry: null,
    deletingEntry: null,
  },

  getters: {
    getResults: (state) => {
      return state.current;
    },
    getEntries: ({ entries }) => entries,
    getData: ({ data }) => data,
    getLayoutData: ({ layoutData }) => layoutData,
    getLayoutPagination:
      ({ layoutPagination }) =>
        (uuid) =>
          layoutPagination?.[uuid] ?? {},
    getMetadataFields: ({ metadataFields }) => metadataFields,
    areResultsLoading: (state) => state.loading,
    isSaving: ({ saving }) => saving,
    getLayoutEntries: ({ layoutEntries }) => layoutEntries,
    getPaginationData:
      ({ pagination }) =>
        (uuid) =>
          pagination?.[uuid] ?? {},
    canEditResults: ({ can }) => Boolean(can?.edit),
    approving: ({ approving }) => approving,
    regenerating: ({ regenerating }) => regenerating,
    editingEntry: ({ editingEntry }) => editingEntry,
    deletingEntry: ({ deletingEntry }) => deletingEntry,
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    setLayoutPagination(state, layoutPagination) {
      state.layoutPagination = { ...state.layoutPagination, ...layoutPagination };
    },

    deleteLayoutPagination(state, project) {
      Vue.delete(state.pagination, project);
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setResults(state, results) {
      state.current = results;
    },

    setEntries(state, entries) {
      state.entries = entries;
    },

    setData(state, data) {
      state.data = data;
    },

    setLayoutData(state, data) {
      state.layoutData = data;
    },

    setMetadataFields(state, metadataFields) {
      state.metadataFields = metadataFields;
    },

    setFilters(state, filters) {
      Vue.set(state, 'currentFilters', { ...filters });
    },

    setLayoutEntries(state, entries) {
      state.layoutEntries = entries;
    },

    deleteEntry(state, entry) {
      Vue.delete(
        state.entries,
        state.entries.findIndex(({ uuid }) => uuid === entry)
      );
    },

    setCan(state, can) {
      Vue.set(state, 'can', can);
    },

    setRegenerating(state, bool) {
      state.regenerating = bool;
    },

    setApproving(state, bool) {
      Vue.set(state, 'approving', bool);
    },

    setEditingEntry(state, entry) {
      Vue.set(state, 'editingEntry', entry);
    },

    setDeletingEntry(state, entry) {
      Vue.set(state, 'deletingEntry', entry);
    },
  },

  actions: {
    async loadResults({ commit }, { project, repository, version }) {
      if (!project || !repository) {
        return;
      }

      let results = null;
      let pagination = {};
      let can = { ...initialCan };

      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.projects.results.current', {
          version: '1.0',
          project,
          repository,
          v: version,
        });

        can = response.data.data.can;
        results = response.data.data;
        pagination = { ...response.data.meta, ...response.data.links };
      } catch (err) {
        console.error(err);
      }

      commit('setCan', can);
      commit('setResults', results);
      commit('setPagination', { [project]: pagination });

      commit('setLoading', false);
    },

    async listenForRecalculation({ commit }, { result: { uuid, version }, onRecalculate }) {
      await listenForRecalculations(
        {
          uuid,
          version: version ?? 1,
        },
        (e) => {
          commit('setRegenerating', false);

          if (onRecalculate) {
            onRecalculate(e);
          }
        },
        () => {
          commit('setRegenerating', true);
        }
      );
    },

    async loadEntries({ commit }, { project, repository, results, version }) {
      if (!project || !repository || !results) {
        return;
      }

      let entries = null;
      let metadataFields = [];

      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.projects.results.entries.index', {
          version: '1.0',
          project,
          repository,
          result: results,
          v: version,
        });

        entries = response.data.data;
        metadataFields = response.data.metadataFields ?? [];
      } catch (err) {
        console.error(err);
      }

      commit('setMetadataFields', metadataFields);
      commit('setEntries', entries);
      commit('setLoading', false);
    },

    async loadData({ commit }, { project, repository, results, version }) {
      if (!project || !repository || !results) {
        return;
      }

      let data = null;

      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.projects.results.data.index', {
          version: '1.0',
          project,
          repository,
          result: results,
          v: version,
        });

        data = response.data.data;
      } catch (err) {
        console.error(err);
      }

      commit('setData', data);
      commit('setLoading', false);
    },

    async approveResult({ commit }, { project, repository, results, data }) {
      if (!project || !repository || !results) {
        return;
      }

      commit('setApproving', true);
      commit('setLoading', true);
      let regenerating = false;
      try {
        await this._vm.$api(
          'repositories.projects.results.sign',
          {
            version: '1.0',
            project,
            repository,
            result: results,
          },
          data
        );
        regenerating = true;
      } catch (err) {
        console.error(err);
      }

      commit('setApproving', false);
      commit('setLoading', false);
      commit('setRegenerating', regenerating);
    },

    async loadLayoutData(
      { commit },
      { project, repository, results, version, filters, page, perPage, sortBy, sortDesc }
    ) {
      if (!project || !repository || !results) {
        return;
      }

      let data = null;
      let layoutPagination = {};

      commit('deleteLayoutPagination', project);
      commit('setLayoutData', []);
      commit('setLoading', true);

      try {
        const response = await this._vm.$api('repositories.projects.results.table', {
          version: '1.0',
          project,
          repository,
          result: results,
          v: version,
          filter: filters,
          perPage,
          page,
          sortBy,
          sortDesc,
        });

        data = response.data.data;
        layoutPagination = { ...response.data.meta, ...response.data.links };

        data.rows = formatRowForRendering(data.rows, data.columns, results);
        commit('setLayoutPagination', { [project]: layoutPagination });
        commit('setLayoutData', data);
        commit('setLoading', false);
      } catch (err) {
        console.error(err);
      }
    },

    async createEntry({ commit, dispatch }, { repository, project, results, data, sourceType }) {
      commit('setSaving', true);
      let newEntry = null;
      const api =
        sourceType === 'event'
          ? 'repositories.projects.results.entries.storeByModule'
          : 'repositories.projects.results.entries.store';

      try {
        const response = await this._vm.$api(
          api,
          {
            version: '1.0',
            project,
            repository,
            result: results,
          },
          data
        );

        newEntry = response.data.data;
      } catch (err) {
        console.error(err);
      }

      if (newEntry) {
        dispatch('loadEntries', { repository, project, results });
      }

      commit('setSaving', false);
    },

    async updateEntry({ commit, dispatch }, { repository, project, results, entry, data }) {
      commit('setSaving', true);
      let updated = null;

      try {
        const response = await this._vm.$api(
          'repositories.projects.results.entries.update',
          {
            version: '1.0',
            project,
            repository,
            result: results,
            entry,
          },
          { ...data }
        );

        updated = response.data.data;
      } catch (err) {
        console.error(err);
      }

      if (updated) {
        dispatch('loadEntries', { repository, project, results });
      }

      commit('setSaving', false);
    },

    async deleteEntry({ commit }, { repository, project, results, entry }) {
      commit('deleteEntry', entry);
      commit('setDeletingEntry', null);

      try {
        await this._vm.$api('repositories.projects.results.entries.destroy', {
          version: '1.0',
          project,
          repository,
          result: results,
          entry,
        });
      } catch (err) {
        console.error(err);
      }
    },

    async updateConfiguration({ commit, dispatch }, { repository, project, results, data }) {
      if (!project || !repository || !results) {
        return;
      }

      commit('setSaving', true);
      try {
        await this._vm.$api(
          'repositories.projects.results.update',
          {
            version: '1.0',
            project,
            repository,
            result: results,
          },
          { ...data }
        );
      } catch (err) {
        console.error(err);
      }

      dispatch('loadResults', { project, repository });
      commit('setSaving', false);
    },

    async saveResultOverride({ commit }, { repository, project, result, entry, subject, stage, override, comment }) {
      if (!project || !repository || !result || !entry || !stage) {
        return;
      }

      commit('setSaving', true);
      try {
        await this._vm.$api(
          'repositories.projects.results.data.store',
          {
            repository,
            project,
            result,
            subject,
            stage,
          },
          { entry, value: override, comment }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    async exportResults({ commit }, { repository, project, result, version, entries, filters, sortBy, sortDesc }) {
      if (!project || !repository || !result) {
        return null;
      }

      let downloadUrl = null;

      commit('setSaving', true);
      try {
        const response = await this._vm.$api(
          'repositories.projects.results.table.export',
          {
            version: '1.0',
            project,
            repository,
            result,
            v: version,
            filter: filters,
            sortBy,
            sortDesc,
          },
          { entries: entries }
        );

        downloadUrl = response.data.data ?? null;
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);

      return downloadUrl;
    },

    async saveComment({ commit }, { repository, project, result, key, comment, subjectUuid, stageUuid, entryUuid }) {
      if (!project || !repository || !result) {
        return null;
      }

      commit('setSaving', true);
      try {
        await this._vm.$api(
          'repositories.projects.results.comments.save',
          {
            repository,
            project,
            result,
            key,
          },
          {
            comment,
            subject_uuid: subjectUuid,
            stage_uuid: stageUuid,
            ...(entryUuid ? { entry_uuid: entryUuid } : {}),
          }
        );
      } catch (err) {
        console.error(err);
      }
      commit('setSaving', false);
    },

    storeFilters({ commit }, filters) {
      commit('setFilters', filters);
    },

    storeLayoutEntries({ commit }, entries) {
      commit('setLayoutEntries', entries);
    },

    setEditingEntry({ commit }, entry) {
      commit('setEditingEntry', entry);
    },

    setDeletingEntry({ commit }, entry) {
      commit('setDeletingEntry', entry);
    },
  },
};
